import { defineStore } from 'pinia';
import { Config } from '@/config';
import apiService from '@/services/api.service';
import { FileUploaderFactory } from '@/factories/file-uploader.factory';
import type { SavingOptions } from '@/services/types/file-uploader.types';

import type { ProjectModuleEventItemConfigDto } from '@/services/types/api.types';
import { Constants } from '@/constants';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import { buildTransportPayload, normalizeEmptyImageAltText } from '@/helpers/store.helper';

/**
 * Finds an event item by its ID in the given array.
 * @param {ProjectModuleEventItemConfigDto[]} events - The array of event items.
 * @param {number} eventId - The ID of the event to find.
 * @returns {ProjectModuleEventItemConfigDto | undefined} The found event or undefined.
 */
const findEvent = (events: ProjectModuleEventItemConfigDto[], eventId: number) => {
  return events.find((x) => x.id === eventId);
};

const buildEventItemPayload = (eventItem: ProjectModuleEventItemConfigDto) => {
  return buildTransportPayload(eventItem, {
    stripKeys: ['ProjectModule', 'created_at', 'updated_at', 'deleted_at'],
    normalize: [(payload) => normalizeEmptyImageAltText(payload.config.image)],
  });
};

/**
 * Returns the list of available status options with translated labels.
 * @returns {{ label: string; value: number }[]} Array of status options.
 */
function getStatuses(): { label: string; value: number }[] {
  const { t } = useI18n();
  return [
    { label: t('project-module.event.status.options.draft'), value: Constants.ProjectModule.EventItem.Status.draft },
    {
      label: t('project-module.event.status.options.published'),
      value: Constants.ProjectModule.EventItem.Status.published,
    },
  ];
}

export interface EventListItemsState {
  eventListItems: ProjectModuleEventItemConfigDto[];
  itemLoadingStates: Set<number>;
}

export const useEventListItemsStore = defineStore({
  id: 'eventListItems',
  state: (): EventListItemsState => ({
    eventListItems: [],
    itemLoadingStates: new Set<number>(),
  }),
  getters: {
    /**
     * Checks if a specific event item is currently loading.
     * @param {EventListItemsState} state - The store state.
     * @returns {(id: number) => boolean} Function that returns true if the item is loading.
     */
    isItemLoading: (state) => (id: number) => state.itemLoadingStates.has(id),

    /**
     * Returns event items sorted by begin_date (ascending).
     * @param {EventListItemsState} state - The store state.
     * @returns {ProjectModuleEventItemConfigDto[]} Sorted array of events.
     */
    orderedEventListItems: (state) => {
      return [...state.eventListItems].sort(
        (a, b) => new Date(a.begin_date).getTime() - new Date(b.begin_date).getTime(),
      );
    },

    /**
     * Returns the list of status options.
     * @returns {{ label: string; value: number }[]} Status options.
     */
    getStatuses: () => getStatuses,

    /**
     * Finds an event item by its ID.
     * @param {EventListItemsState} state - The store state.
     * @returns {(eventId: number) => ProjectModuleEventItemConfigDto | undefined} Function that returns the event or undefined.
     */
    getEvent: (state) => (eventId: number) => {
      return findEvent(state.eventListItems, eventId);
    },

    /**
     * Retrieves a single event item directly by ID (alternative to getEvent).
     * @param {EventListItemsState} state - The store state.
     * @returns {(id: number) => ProjectModuleEventItemConfigDto | undefined} Function that returns the event or undefined.
     */
    getItem:
      (state) =>
      (id: number): ProjectModuleEventItemConfigDto | undefined =>
        state.eventListItems.find((e) => e.id === id),
  },
  actions: {
    /**
     * Fetches all event items for a given project module.
     * @param {number} projectModuleId - The ID of the project module.
     */
    async fetchEventListItemsByModuleId(projectModuleId: number) {
      this.itemLoadingStates.add(projectModuleId);
      try {
        const { data } = await apiService.get<ProjectModuleEventItemConfigDto[]>(
          `${Config.api.v2}/project-modules/${projectModuleId}/event-items`,
        );
        this.eventListItems = data;
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(projectModuleId);
      }
    },

    /**
     * Fetches a single event item by its ID and updates/adds it to the store.
     * @param {number} id - The ID of the event item.
     */
    async fetchEvent(id: number) {
      this.itemLoadingStates.add(id);
      try {
        const { data } = await apiService.get<ProjectModuleEventItemConfigDto>(`${Config.api.v2}/event-items/${id}`);
        const index = this.eventListItems.findIndex((e) => e.id === id);
        if (index !== -1) {
          this.eventListItems[index] = data;
        } else {
          this.eventListItems.push(data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.itemLoadingStates.delete(id);
      }
    },

    /**
     * Creates a new empty event item object (not persisted yet).
     * @param {number} projectModuleId - The ID of the parent project module.
     * @returns {ProjectModuleEventItemConfigDto} New event item with default values.
     */
    createEventListItem(projectModuleId: number): ProjectModuleEventItemConfigDto {
      return {
        project_module_id: projectModuleId,
        title: { en: '', fr: '' },
        description: { en: '', fr: '' },
        begin_date: new Date().toISOString(),
        end_date: null,
        start_time: '',
        end_time: '',
        location: { en: '', fr: '' },
        config: {
          image: null,
          ctaButtons: {
            button1: {
              enabled: false,
              text: { en: '', fr: '' },
              linkType: 'url',
              url: { en: '', fr: '' },
              moduleId: undefined,
            },
          },
        },
        status: Constants.ProjectModule.EventItem.Status.draft,
      };
    },

    /**
     * Saves (creates or updates) an event item and refreshes the list.
     * On creation, navigates to the event details route.
     * @param {ProjectModuleEventItemConfigDto} eventListItem - The event item to save.
     * @returns {Promise<boolean>} True if save was successful.
     */
    async saveEventItem(eventListItem: ProjectModuleEventItemConfigDto, project_id: number): Promise<boolean> {
      try {
        const payload = buildEventItemPayload(eventListItem);
        let eventId = eventListItem.id;
        if (eventId) {
          await apiService.put<ProjectModuleEventItemConfigDto>(`${Config.api.v2}/event-items/${eventId}`, payload);
        } else {
          const { data } = await apiService.post<ProjectModuleEventItemConfigDto>(
            `${Config.api.v2}/event-items`,
            payload,
          );
          eventId = data.id;

          // Navigate to details after creation (adjust route name if needed)
          router.push({
            name: 'event_item_details',
            params: { id: eventId, projectId: project_id, moduleId: eventListItem.project_module_id },
          });
        }
        await this.fetchEvent(eventId!);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },

    /**
     * Deletes an event item and refreshes the list.
     * @param {ProjectModuleEventItemConfigDto} eventListItem - The event item to delete.
     */
    async deleteEventListItem(eventListItem: ProjectModuleEventItemConfigDto) {
      if (!eventListItem.id) return;
      try {
        await apiService.delete(`${Config.api.v2}/event-items/${eventListItem.id}`);
        await this.fetchEventListItemsByModuleId(eventListItem.project_module_id);
      } catch (error) {
        console.error(error);
      }
    },

    /**
     * Bulk updates multiple event items (e.g., for reordering).
     * @param {...ProjectModuleEventItemConfigDto[]} eventListItems - Event items to update.
     */
    async updateEventListItems(...eventListItems: ProjectModuleEventItemConfigDto[]) {
      try {
        for (const eventListItem of eventListItems) {
          if (eventListItem.id) {
            await apiService.put(`${Config.api.v2}/event-items/${eventListItem.id}`, eventListItem);
          }
        }
        // Refresh the full list after bulk update
        if (eventListItems.length > 0) {
          await this.fetchEventListItemsByModuleId(eventListItems[0].project_module_id);
        }
      } catch (error) {
        console.error(error);
      }
    },

    /**
     * Uploads a new image/banner for the event item.
     * @param {number} eventId - The ID of the event item.
     * @param {SavingOptions} options - File uploader options.
     */
    async saveImage(eventId: number, options: SavingOptions) {
      try {
        await FileUploaderFactory.create(options.version).upload({
          files: options.files,
          metadata: options.metadata,
          url: `${Config.api.v2}/event-items/${eventId}/image`,
        });
        await this.fetchEvent(eventId);

        return true;
      } catch (error) {
        console.error(error);
        return false;
      }
    },

    /**
     * Removes the image/banner from the event item.
     * @param {number} eventId - The ID of the event item.
     */
    async removeImage(eventId: number) {
      try {
        await apiService.delete(`${Config.api.v2}/event-items/${eventId}/image`);
        await this.fetchEvent(eventId);
      } catch (error) {
        console.error(error);
      }
    },
  },
});
